/**
 * The word filter (ADR-0156: the App Store's rule for what players write): a name or a program code with a rude word in
 * it is refused (`nameProblem`, `isRude`), a rude word in a chat message is starred out (`maskRude`, on the server, so
 * every client gets it clean). Shared by the server and the client (a name is checked as it is typed).
 *
 * Words are compared as letters only, upper case, accents off and look-alike digits read as letters (`F4G`, `$H1T`);
 * a run of one- and two-letter bits is read as one word (`F.U.C.K`). `ROOTS` count anywhere in a word (`BIGFUCK`), but
 * inside a word of `ALLOWED` (`SCUNTHORPE`, `COCKTAIL`); `WORDS` count only as a whole word, a plural too (`ASS`, not
 * `GRASS`). Roots with no double letter are also found stretched out (`FUUUCK`).
 * ponytail: a fixed list (English and Polish, the game's players); other languages and chat's spaced-out letters pass.
 * A player's report reaches a person (ADR-0156) for what it misses; a bigger list or a service when that isn't enough.
 */

const words = (s: string): readonly string[] => s.trim().split(/\s+/);

const ROOTS = words(`
  FUCK SHIT CUNT NIGGER NIGGA FAGGOT COCK DICK PUSSY PENIS VAGINA WHORE SLUT BITCH BASTARD ASSHOLE ARSEHOLE RAPE RAPIST
  RETARD TRANNY KIKE CHINK WETBACK HITLER NAZI SIEGHEIL KKK PEDO PAEDO MOLEST PORN HENTAI DILDO JIZZ ORGASM WANK TWAT
  KILLYOURSELF
  KURW CHUJ JEBA JEBIE JEBAN PIERDOL PIERDAL PIZD CWEL DZIWK KUTAS PEDZIO
`);
const WORDS = new Set(
  words(`
  ASS ASSES ARSE CUM SEX SEXY TITS TITTIES BOOB BOOBS ANAL FAG DYKE NIG NIGA SPIC GOOK PAKI COON FCK FUK FUQ KYS THOT
  CIPA FIUT CIOTA SZMATA HUJ PUTA
`),
);
const ALLOWED = words(`
  SCUNTHORPE COCKATOO COCKATIEL COCKPIT COCKROACH COCKTAIL COCKEREL COCKLE COCKNEY PEACOCK HANCOCK HITCHCOCK WOODCOCK
  SHUTTLECOCK GAMECOCK DICKENS DICKINSON DICKSON GRAPE DRAPE TRAPE SCRAPE PARAPET RAPESEED THERAPIST TORPEDO SWANK
  PUSSYCAT SHITAKE RETARDANT
`);

/** Look-alikes read as the letter they stand for. */
const LEET: Record<string, string> = {
  '0': 'O',
  '1': 'I',
  '3': 'E',
  '4': 'A',
  '5': 'S',
  '7': 'T',
  '8': 'B',
  '@': 'A',
  $: 'S',
  '!': 'I',
};

/** `raw` as the filter reads it: upper case, accents off (Ł too), look-alikes as letters, nothing but A-Z. */
function letters(raw: string): string {
  return raw
    .toUpperCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/Ł/g, 'L')
    .replace(/[0-9@$!]/g, (c) => LEET[c] ?? c)
    .replace(/[^A-Z]/g, '');
}

/** Every run of one letter as one: `FUUUCK` → `FUCK`. */
const squeeze = (s: string): string => s.replace(/([A-Z])\1+/g, '$1');
/** Roots with no double letter: found in a squeezed word too (one with a double, `NIGGER`, would find `NIGERIA`). */
const SQUEEZABLE = ROOTS.filter((r) => squeeze(r) === r);

/** Does one word (as `letters` reads it) say something rude? */
function rudeWord(word: string): boolean {
  if (word.length === 0) return false;
  if (WORDS.has(word) || (word.endsWith('S') && WORDS.has(word.slice(0, -1)))) return true;
  // The allowed words' letters can't be part of a rude one.
  let w = word;
  for (const a of ALLOWED) if (w.includes(a)) w = w.replaceAll(a, '.');
  if (ROOTS.some((r) => w.includes(r))) return true;
  const s = squeeze(w);
  return SQUEEZABLE.some((r) => s.includes(r));
}

/** A word as typed: letters, digits and the look-alikes. One with no letter (a number, `455`) is never rude. */
const WORD = /[\p{L}\p{N}@$!]+/gu;
const hasLetter = (s: string) => /\p{L}/u.test(s);

/** Is there a rude word in `text` (a name, a program code)? Each word, and each run of short bits read as one. */
export function isRude(text: string): boolean {
  let run = '';
  for (const raw of text.match(WORD) ?? []) {
    if (!hasLetter(raw)) continue;
    const w = letters(raw);
    if (w.length <= 2) {
      run += w;
      continue;
    }
    if (rudeWord(run) || rudeWord(w)) return true;
    run = '';
  }
  return rudeWord(run);
}

/** `text` with each rude word starred out, letter for letter (`what the ****`); anything else as it was. */
export function maskRude(text: string): string {
  return text.replace(WORD, (w) => (hasLetter(w) && rudeWord(letters(w)) ? '*'.repeat([...w].length) : w));
}
